import React from "react";
import { object, shape, string, any, arrayOf } from "prop-types";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { BRAND_NAMES } from "@insightfulscience/contentful/constants";
import { useLocale } from "@insightfulscience/smart-react/i18";
import Hero from "@insightfulscience/atomic-react/Hero";
import { Col, Container, Row } from "@insightfulscience/atomic-react/Grid";
import Image from "@insightfulscience/atomic-react/Image";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import Section from "@insightfulscience/atomic-react/Section";
import ContentContainer from "@insightfulscience/atomic-react/r1/atoms/ContentContainer";
import Line from "@insightfulscience/atomic-react/Line";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import GuidePageTemplateSidebar from "@insightfulscience/atomic-react/GuidePageTemplate/Sidebar";
import {
	GuidePageTemplateNavItem,
	GuidePageTemplateNavItemLink,
} from "@insightfulscience/atomic-react/GuidePageTemplate/Nav";
import { scrollToHeading } from "@insightfulscience/atomic-react/utils/Guides";

import Link from "@insightfulscience/atomic-react/Link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
	generateHeadingId,
	sidebarNavigation,
} from "@insightfulscience/contentful/richTextTypes/default/utils";
import richTextTypes from "@insightfulscience/contentful/richTextTypes/default";
import renderBlockByType from "@insightfulscience/contentful/utils/renderBlockByType";
import Heading from "@insightfulscience/atomic-react/Heading";
import RecommendedVideos from "@insightfulscience/contentful/ui/RecommendedVideos";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import Block from "@insightfulscience/atomic-react/Block";
import Button from "@insightfulscience/atomic-react/Button";
import routes from "../../../routes";

const logoAcademy = "/images/geneious/logo/logo-geneious-academy.svg";
const GuidesPage = ({ post, pageProps }) => {
	const [t] = useLocale("guides");
	const { siteDomain, brandCode } = pageProps;
	const {
		title,
		subtitle = "",
		lead = "",
		metaDescription = "",
		slug,
		body,
		thumbnail,
		additionalResources = [],
		recommendedVideos = [],
		toCDownloadEbookLink = "",
	} = post;

	const thumbnailUrl = thumbnail?.fields?.file?.url || "";
	const navigation = sidebarNavigation(body);
	const totalHeading2 = body.content.reduce((total, current) => {
		return total + (current.nodeType === "heading-2" ? 1 : 0);
	}, 0);
	const isSidebar = totalHeading2 > 0;
	const brandName = BRAND_NAMES[brandCode];
	const description = subtitle || lead || "";
	return (
		<>
			<PageMeta title={`${title} - ${brandName}`} description={metaDescription}>
				<PageMeta.OGP
					title={`${title} - ${brandName}`}
					description={metaDescription}
					url={`${siteDomain}/guides/${slug}`}
					image={thumbnailUrl || t("META_IMAGE")}
					imageSecureUrl={thumbnailUrl || t("META_IMAGE_SECURE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Twitter
					card={t("META_TWITTER_CARD")}
					site={t("META_TWITTER_SITE")}
					creator={t("META_TWITTER_CREATOR")}
					title={`${title} - ${brandName}`}
					description={metaDescription}
					image={thumbnailUrl || t("META_TWITTER_IMAGE")}
					isImageBaseUrl={!thumbnailUrl}
				/>
				<PageMeta.Canonical href={`${siteDomain}/guides/${slug}`} />
			</PageMeta>
			<Hero color="white" spacing={{ pb: 10 }}>
				<Row utility={{ justify: "center" }} spacing={{ mb: 6 }}>
					<Col lg={5} md={5} sm={5}>
						<Link href={routes.external.academy}>
							<Image src={logoAcademy} isBaseUrl />
						</Link>
					</Col>
				</Row>
				<HeroTitle weight="medium" color="black">
					{title}
				</HeroTitle>
				{description && <HeroDescription>{description}</HeroDescription>}
			</Hero>
			<Section spacing={{ sm: { mb: 10 } }}>
				<ContentContainer lg={12}>
					<Line />
				</ContentContainer>
			</Section>
			<GuidePageTemplate>
				{isSidebar && (
					<GuidePageTemplateSidebar spacing={{ mb: 6 }}>
						{toCDownloadEbookLink && (
							<GuidePageTemplateNavItem>
								<Block spacing={{ mb: 10 }}>
									<Button.Link
										href={toCDownloadEbookLink}
										isPreLine
										size="s"
										isBlock
										spacing={{ mb: 8 }}
									>
										{t("DOWNLOAD.btn_text")}
									</Button.Link>
									<Paragraph center spacing={{ mb: 8 }}>
										{t("DOWNLOAD.copy")}
									</Paragraph>
									<Line />
								</Block>
							</GuidePageTemplateNavItem>
						)}

						{navigation.map(item => {
							return (
								<GuidePageTemplateNavItem key={item}>
									<GuidePageTemplateNavItemLink
										href={`#${generateHeadingId(item)}`}
										onClick={e => scrollToHeading(e)}
									>
										{item}
									</GuidePageTemplateNavItemLink>
								</GuidePageTemplateNavItem>
							);
						})}
					</GuidePageTemplateSidebar>
				)}
				<GuidePageTemplateMain>
					{documentToReactComponents(body, richTextTypes)}
				</GuidePageTemplateMain>
			</GuidePageTemplate>
			{recommendedVideos.length > 0 && (
				<Section spacing={{ py: 16 }}>
					<Heading Tag="h2" sx={{ fontSize: "l", textAlign: "center" }}>
						Recommended Resources
					</Heading>
					<Container>
						<RecommendedVideos brandCode={brandCode} items={recommendedVideos} />
					</Container>
				</Section>
			)}
			{additionalResources.map(renderBlockByType(slug))}
		</>
	);
};

GuidesPage.propTypes = {
	post: shape({
		title: string.isRequired,
		subtitle: string,
		metaDescription: string,
		slug: string.isRequired,
		lead: string,
		body: object, // eslint-disable-line react/forbid-prop-types,
		toCLink: string,
		toCDownloadEbookLink: string,
	}),
	pageProps: shape({
		resources: any, // eslint-disable-line react/forbid-prop-types,
		siteDomain: string.isRequired,
		brandCode: string,
		breadcrumbs: arrayOf(
			shape({
				title: string.isRequired,
				href: string.isRequired,
			}),
		),
	}),
};

export default GuidesPage;
